import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import CustomRadio from '../common/CustomRadio';
import NewCustomInput from '../common/NewCustomInput';
import CustomSelect from '../common/CustomSelect';
import { BsPlusLg } from 'react-icons/bs';
import { TfiMinus } from 'react-icons/tfi';

const Div3 = ({ formData, setFormData, errors, setErrors, handleChange, handleFontSizeChange }) => {
  const [visibleLines, setVisibleLines] = useState([1]);
  const fontSizeOptions = ['6pt', '7pt', '8pt', '9pt', '10pt', '11pt', '12pt', '13pt', '14pt', '15pt', '16pt'];
  const defaultFontSize = '6pt';

  // Debug log to verify hlinebg changes (comment out in production)
  console.log('Div3 hlinebg:', formData.hlinebg);

  // Make visibleLines reactive to incoming formData (fixes Edit mode)
  useEffect(() => {
    const lines = [1];
    if (formData.hline2?.trim()) lines.push(2);
    if (formData.hline3?.trim()) lines.push(3);
  
    // Only update visibleLines if it's the initial mount OR edit mode data just loaded
    setVisibleLines(prev => {
      if (prev.length === 1 || lines.length > prev.length) {
        return lines;
      }
      return prev;
    });
  }, [formData.hline2, formData.hline3]);
  


  const [isAdding, setIsAdding] = useState(false);

  const handleAddLine = useCallback(() => {
    if (isAdding) return; // block repeated clicks
    setIsAdding(true);

    // Read current DOM values to avoid stale React state (prevents needing a second click)
    const h1 = (document.getElementById('hline1')?.value || '').trim();
    const h2 = (document.getElementById('hline2')?.value || '').trim();

    if (visibleLines.length === 1 && !h1) {
      setErrors((prev) => ({ ...prev, hline1: 'Please fill Heading Line 1 before adding another line.' }));
      document.getElementById('hline1')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // small delay before allowing another add
      setTimeout(() => setIsAdding(false), 50);
      return;
    }

    if (visibleLines.length === 2 && !h2) {
      setErrors((prev) => ({ ...prev, hline2: 'Please fill Heading Line 2 before adding another line.' }));
      document.getElementById('hline2')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setIsAdding(false), 50);
      return;
    }

    setVisibleLines((prev) => {
      if (prev.length < 3) {
        const newLine = Math.max(...prev) + 1;
        setFormData((fPrev) => ({ ...fPrev, [`hline${newLine}`]: '', [`hline${newLine}fs`]: defaultFontSize }));
        return [...prev, newLine];
      }
      return prev;
    });

    // Delay allowing another click until after render
    setTimeout(() => setIsAdding(false), 100);
  }, [visibleLines, setFormData, setErrors, isAdding]);


  const handleRemoveLine = useCallback(
    (line) => {
      if (line > 1) {
        setVisibleLines((prev) => prev.filter((l) => l !== line));
        setFormData((prev) => ({
          ...prev,
          [`hline${line}`]: '',
          [`hline${line}fs`]: '',
        }));
        setErrors((prev) => {
          const next = { ...prev };
          delete next[`hline${line}`];
          delete next[`hline${line}_fontSize`];
          return next;
        });
      }
    },
    [setFormData, setErrors]
  );


  const renderStyledHeading = () => {
    const h1 = formData.hline1?.trim();
    const h2 = formData.hline2?.trim();
    const h3 = formData.hline3?.trim();

    if (!h1) {
      return (
        <span
          className={`text-xl ${
            (formData.hlinebg ?? 'Normal') === 'Dark'
              ? 'bg-gray-900 text-white'
              : 'bg-white text-gray-900'
          }`}
        >
          No heading text provided
        </span>
      );
    }
    
    const lines = [
      { text: h1, fontSize: formData.hline1fs || defaultFontSize },
      ...(h2 ? [{ text: h2, fontSize: formData.hline2fs || defaultFontSize }] : []),
      ...(h3 ? [{ text: h3, fontSize: formData.hline3fs || defaultFontSize }] : []),
    ];

    return (
      <div className="flex flex-col items-center justify-center gap-1">
        {lines.map((line, idx) => (
          <span key={idx} style={{ fontSize: line.fontSize }} className="text-center block">
            {line.text}
          </span>
        ))}
      </div>
    );
  };



  return (
    <div className="mb-6 border-b border-gray-300 pb-4">
      <h3 className="text-lg text-gray-900 font-semibold mb-2">Heading Details</h3>
      <div className="flex items-center gap-4 mb-4">
        <span className="text-gray-900 font-bold text-base">Background:</span>
        <CustomRadio
          name="hlinebg"
          options={['Normal', 'Dark']}
          value={formData.hlinebg}
          checked={(formData.hlinebg || 'Normal')}
          onChange={handleChange}
          error={errors.hlinebg}
          required
        />
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="flex-grow">
            <NewCustomInput
              id="hline1"
              label="Enter heading line 1"
              name="hline1"
              type="text"
              value={formData.hline1}
              onChange={handleChange}
              placeholder="Enter heading line 1"
              error={errors.hline1}
              required
            />
          </div>
          <div className="w-24 -mt-4">
            <CustomSelect
              label={<span>Font Size</span>}
              name="hline1_fontSize"
              options={fontSizeOptions}
              value={formData.hline1fs}
              onChange={handleFontSizeChange('hline1')}
              error={errors.hline1_fontSize}
            />
          </div>

          {visibleLines.length < 3 && (
            <button
              type="button"
              onClick={handleAddLine}
              className="text-gray-900 hover:bg-gray-200 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              aria-label="Add next line"
            >
              <BsPlusLg size={24} />
            </button>
          )}
        </div>
        {visibleLines.includes(2) && (
          <div className="flex items-center gap-2">
            <div className="flex-grow">
              <NewCustomInput
                id="hline2"
                label="Enter heading line 2"
                name="hline2"
                type="text"
                value={formData.hline2}
                onChange={handleChange}
                placeholder="Enter heading line 2"
                error={errors.hline2}
              />
            </div>
            <div className="w-24">
              <CustomSelect
                label={<span>Font Size</span>}
                name="hline2_fontSize"
                options={fontSizeOptions}
                value={formData.hline2fs}
                onChange={handleFontSizeChange('hline2')}
                error={errors.hline2_fontSize}
              />
            </div>
            <div className="flex gap-2">
              {visibleLines.length < 3 && (
                <button
                  type="button"
                  onClick={handleAddLine}
                  className="text-gray-900 hover:bg-gray-200 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  aria-label="Add next line"
                >
                  <BsPlusLg size={24} />
                </button>
              )}
              <button
                type="button"
                onClick={() => handleRemoveLine(2)}
                className="text-gray-900 hover:bg-gray-200 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                aria-label="Remove line 2"
              >
                <TfiMinus size={24} />
              </button>
            </div>
          </div>
        )}
        {visibleLines.includes(3) && (
          <div className="flex items-center gap-2">
            <div className="flex-grow">
              <NewCustomInput
                id="hline3"
                label="Enter heading line 3"
                name="hline3"
                type="text"
                value={formData.hline3}
                onChange={handleChange}
                placeholder="Enter heading line 3"
                error={errors.hline3}
              />
            </div>
            <div className="w-24">
              <CustomSelect
                label={<span>Font Size</span>}
                name="hline3_fontSize"
                options={fontSizeOptions}
                value={formData.hline3fs}
                onChange={handleFontSizeChange('hline3')}
                error={errors.hline3_fontSize}
              />
            </div>
            <button
              type="button"
              onClick={() => handleRemoveLine(3)}
              className="text-gray-900 hover:bg-gray-200 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              aria-label="Remove line 3"
            >
              <TfiMinus size={24} />
            </button>
          </div>
        )}
      </div>
      <div className="mt-4">
        <label className="block text-gray-900 font-semibold">Heading Preview:</label>
        <div
          className={`w-full leading-none border border-gray-300 rounded-md py-2 px-3 ${(formData.hlinebg ?? 'Normal') === 'Dark' ? 'bg-gray-900 text-white' : 'white text-gray-900'
            }`}
          aria-label="Heading preview"
        >
          {renderStyledHeading()}
        </div>
      </div>
    </div>
  );
};

Div3.propTypes = {
  formData: PropTypes.shape({
    hlinebg: PropTypes.string,
    hline1: PropTypes.shape({
      text: PropTypes.string,
      fontSize: PropTypes.string,
    }),
    hline2: PropTypes.shape({
      text: PropTypes.string,
      fontSize: PropTypes.string,
    }),
    hline3: PropTypes.shape({
      text: PropTypes.string,
      fontSize: PropTypes.string,
    }),
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleFontSizeChange: PropTypes.func.isRequired,
};

export default Div3;
